/** biome-ignore-all lint/style/useNamingConvention: External API payloads */

import type { JSX } from 'react';
import type { LucideIconName } from '@/app_components/icons/constants';
import type { CardType, PaymentMethodType } from '@/app_lib/types/accounts/Billing';
import type { Plan } from '@/app_lib/types/accounts/recurly/Plan';
import type { RecurlyFieldOptions } from './recurly/Account';

interface PianoResource {
  name: string;
  rid: string;
}

interface PianoCustomField {
  fieldName: string;
  value: string;
}

interface TextCustomField extends PianoCustomField {
  options: string[];
  dataType: 'TEXT';
}

interface CheckboxCustomField extends PianoCustomField {
  options: string[];
  dataType: 'BOOLEAN';
}

interface SingleSelectCustomField extends PianoCustomField {
  options: string[];
  dataType: 'SINGLE_SELECT_LIST';
}

// As can be seen here: https://docs.piano.io/api?endpoint=post~2F~2Fpublisher~2FcustomField
export type PianoCustomFieldData = (TextCustomField | SingleSelectCustomField | CheckboxCustomField)[];

// Named for PublisherCustomFieldResponse type (https://docs.piano.io/api?endpoint=post~2F~2Fpublisher~2Fusers~2Fget)
export type PianoPublisherCustomField = { name: string; value: string };

export enum MyAccountPages {
  Home = 'home',
  Profile = 'profile',
  Billing = 'billing',
  Subscriptions = 'subscriptions',
  Newsletters = 'newsletters',
  ReasonsToBelieve = 'reasons-to-believe',
  Canceled = 'canceled',
  Paused = 'paused',
  Downgraded = 'downgraded',
  Terminated = 'terminated',
}

export const MyAccountPageTitles = {
  [MyAccountPages.Home]: 'My Profile',
  [MyAccountPages.Profile]: 'My Profile',
  [MyAccountPages.Billing]: 'Billing',
  [MyAccountPages.Subscriptions]: 'Subscription Overview',
  [MyAccountPages.Newsletters]: 'Newsletters',
};

export type UserAddress = {
  firstName: string;
  lastName: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export const pianoCustomFieldMap = {
  country: 'country',
  experienceLevel: 'experience-level',
  industry: 'industry',
  jobFunction: 'job-function',
  prefix: 'salutation',
};

export type PianoFieldKey = keyof typeof pianoCustomFieldMap;

export type PianoFieldValue = (typeof pianoCustomFieldMap)[PianoFieldKey];

export type SelectFieldOptions = Record<PianoFieldValue, { label: string; value: string }[]>;

export interface PianoCustomFields {
  country: string;
  'experience-level': string;
  industry: string;
  'job-function': string;
  salutation: string;
}

export type MainAccessData = { status: SubscriptionStatus; type: string } | undefined;

export interface PersonalInfoData {
  email: string;
  firstName: string;
  lastName: string;
  country: string;
  experienceLevel: string;
  industry: string;
  jobFunction: string;
  prefix: string;
  passwordless: boolean;
  pianoUid: string;
}

export type MyProfileData = {
  personalInfo: PersonalInfoData;
  address: UserAddress | null;
  mainAccessData: MainAccessData;
  pianoFieldOptions: SelectFieldOptions;
  recurlyFieldOptions: RecurlyFieldOptions;
};

export type UpdatedFields = Partial<PersonalInfoData>;

export interface PianoAccess {
  granted: boolean;
  resource: PianoResource;
  expire_date: number;
}

export enum PianoSubStatusMap {
  active = 'Active',
  completed = 'Expiring',
  expired = 'Expired',
}

export enum RecurlySubStatusMap {
  active = 'Active',
  canceled = 'Expiring',
  expired = 'Expired',
  paused = 'Paused',
}

export interface PianoSubscription {
  end_date: number;
  status: keyof typeof PianoSubStatusMap;
  subscription_id: string;
  start_date: number;
  term: {
    type: string;
    resource: PianoResource;
  };
}

export interface PianoConversion {
  user_access: {
    expire_date: number;
    granted: boolean;
    start_date: number;
  };
  term: {
    description: string;
    name: string;
  };
  term_conversion_id: string;
}

export interface RecurlySubscriptionCoupon {
  id: string;
  name: string;
  code: string;
}

export interface RecurlySubscriptionCouponRedemption {
  coupon: RecurlySubscriptionCoupon;
  state: 'active' | 'inactive';
}

export interface RecurlySubscriptionRampInterval {
  starting_on: string;
  ending_on: string | null;
  unit_amount: number;
  remaining_billing_cycles: number;
  starting_billing_cycle: number;
}

export interface RecurlySubscriptionPendingChange {
  subscription_id: string;
  id: string;
  plan: Plan;
  ramp_intervals: RecurlySubscriptionRampInterval[];
}

export interface RecurlySubscription {
  coupon_redemptions: RecurlySubscriptionCouponRedemption[] | null;
  custom_fields: { name: string; value: string }[];
  uuid: string;
  created_at: string;
  current_billing_cycle: number;
  current_period_started_at: string;
  current_period_ends_at: string;
  current_term_started_at: string;
  current_term_ends_at: string;
  expires_at: string;
  state: keyof typeof RecurlySubStatusMap;
  plan: Plan;
  ramp_intervals: RecurlySubscriptionRampInterval[];
  pending_change?: RecurlySubscriptionPendingChange;
  tax_info?: {
    rate: number;
  };
  paused_at?: string;
  unit_amount: number;
}

export interface PianoUserDataLimited {
  custom_fields: PianoPublisherCustomField[];
  email: string;
  first_name: string;
  last_name: string;
}

export interface PianoUserData {
  custom_fields: PianoCustomFieldData;
  email: string;
  first_name: string;
  last_name: string;
}

export type SubscriptionStatus = 'Active' | 'Expiring' | 'Expired' | 'Paused';

export interface RenewalPricing {
  amountPreTax: number;
  amountWithTax: number;
}

export type NextRenewalData = RenewalPricing & {
  date: string;
  longDate: string;
};

export interface SubscriptionPageData {
  periodEndsOnLongDate?: string;
  status: SubscriptionStatus;
  type: string;
  currentPeriod?: string;
  currentTermEndsAt?: string;
  periodEndsOn: string;
  startedOn: string;
  nextRenewal?: NextRenewalData;
  id: string;
  inTrial: boolean;
  pausedAt?: string;
  hasPendingChange?: RecurlySubscriptionPendingChange;
  hasActiveDowngradeCoupon?: boolean;
  isMoonpay?: boolean;
}

export interface AccountInvoice {
  amount: number;
  date: string;
  number: string;
  period: string;
  subscription: string;
  viewUrl: string;
}

export interface RecurlyBillingInfo {
  id: string;
  first_name: string;
  last_name: string;
  address: {
    country: string;
    postal_code: string;
  };
  primary_payment_method: boolean;
  backup_payment_method: boolean;
}

export interface RecurlyCreditCardPaymentMethod extends RecurlyBillingInfo {
  payment_method: {
    object: 'credit_card';
    card_type: string;
    last_four: string;
    exp_month: number;
    exp_year: number;
  };
}

export interface RecurlyOtherPaymentMethod extends RecurlyBillingInfo {
  payment_method: {
    object: 'braintree_apple_pay' | 'braintree_v_zero' | 'google_pay';
  };
}

export type RecurlyPaymentMethod = RecurlyCreditCardPaymentMethod | RecurlyOtherPaymentMethod;

export interface AccountCreditCardPaymentMethod {
  type: PaymentMethodType.CreditCard;
  cardType: CardType;
  country: string;
  expMonth: string;
  expYear: string;
  firstName: string;
  id: string;
  isPrimary: boolean;
  isBackup: boolean;
  lastFour: string;
  lastName: string;
  postalCode: string;
}

export interface AccountOtherPaymentMethod {
  id: string;
  isPrimary: boolean;
  isBackup: boolean;
  type: Exclude<PaymentMethodType, PaymentMethodType.CreditCard>;
}

export type AccountPaymentMethod = AccountCreditCardPaymentMethod | AccountOtherPaymentMethod;

export type LabeledAccountPaymentMethod = AccountPaymentMethod & {
  label: JSX.Element;
};

export interface RecurlyInvoice {
  origin: string;
  total: number;
  number: string;
  closed_at: string;
  line_items: {
    description: string;
    start_date: string;
    end_date: string;
  }[];
}

export interface ReasonsToBelieveCardContent {
  title: string;
  icon: LucideIconName;
  bullets: FormattedTextOption[][];
  link: string;
}

export interface ReasonsToBelieveCardsProps {
  cardContents: ReasonsToBelieveCardContent[];
}

export interface FormattedTextOption {
  text: string;
  bold?: boolean;
  href?: string;
}

export type UpdateRecurlySubscriptionResult =
  | {
      success: true;
      updatedSubscription: SubscriptionPageData;
    }
  | {
      success: false;
    };

export type HTTPMethod = 'POST' | 'GET' | 'PUT' | 'DELETE';
